import { useCallback, type ReactElement } from 'react'

import { useTranslation } from 'react-i18next'
import type { ViewProductGpsrInfoStore } from '../../../AppData/_stores/ViewProductStore'
import { useAppSelector } from '../../../Common/_hooks/useAppSelector'
import {
  InfoField,
  InfoLabel,
  InfoSection,
  InfoSectionFields,
  InfoSectionTitle,
  InfoValue,
  ProductInfoViewProductList,
  type RenderedProductInfoViewProduct
} from './ProductInfoLayout'
import useProductInfoViewProducts from './useProductInfoViewProducts'

interface Props {
  idProduct: number
}

export default function ProductGpsrTab(props: Props) {
  const [t] = useTranslation('account-v2')
  const countries = useAppSelector(state => state.get('appData').get('countries'))
  const gpsrManufacturers = useAppSelector(state => state.get('appData').get('gpsrManufacturers'))
  const gpsrContacts = useAppSelector(state => state.get('appData').get('gpsrContacts'))
  const viewProducts = useProductInfoViewProducts(props.idProduct)

  const getGpsrContent = useCallback((info: ViewProductGpsrInfoStore): ReactElement | null => {
    const hasManufacturing = countries.get(String(info.get('id_country_of_manufacture')))
      || gpsrManufacturers.get(String(info.get('id_manufacturer')))?.get('name')
      || gpsrManufacturers.get(String(info.get('id_manufacturer')))?.get('address')
      || info.get('manufacturing_method')
      || info.get('manufacturing_value_added')
      || gpsrManufacturers.get(String(info.get('id_manufacturer')))?.get('ca_number')
      || info.get('hs_code')
      || info.has('usmca_compliant')
    const hasGpsrContact = gpsrContacts.get(String(info.get('id_gpsr_contact')))?.get('name')
      || gpsrContacts.get(String(info.get('id_gpsr_contact')))?.get('address')
      || gpsrContacts.get(String(info.get('id_gpsr_contact')))?.get('email')
      || gpsrContacts.get(String(info.get('id_gpsr_contact')))?.get('phone_number')
    const hasSafety = info.get('flammability_test_year') || info.get('risks') || info.get('risk_management')

    if (!hasManufacturing && !hasGpsrContact && !hasSafety && !info.get('warranty')) {
      return null
    }

    return <>
      {hasManufacturing ? <InfoSection>
        <InfoSectionTitle>{t('Manufacturing')}</InfoSectionTitle>
        <InfoSectionFields>
          {countries.get(String(info.get('id_country_of_manufacture'))) ? <InfoField>
            <InfoLabel>{t('Country of manufacture')}</InfoLabel>
            <InfoValue>{t('Made in {{country}}', {
              country: countries.get(String(info.get('id_country_of_manufacture')))?.get('name')
            })}</InfoValue>
          </InfoField> : null}
          {gpsrManufacturers.get(String(info.get('id_manufacturer')))?.get('name') ? <InfoField>
            <InfoLabel>{t('Manufacturer')}</InfoLabel>
            <InfoValue>{gpsrManufacturers.get(String(info.get('id_manufacturer')))?.get('name')}</InfoValue>
          </InfoField> : null}
          {gpsrManufacturers.get(String(info.get('id_manufacturer')))?.get('address') ? <InfoField>
            <InfoLabel>{t('Manufacturer address')}</InfoLabel>
            <InfoValue>{gpsrManufacturers.get(String(info.get('id_manufacturer')))?.get('address')}</InfoValue>
          </InfoField> : null}
          {info.get('manufacturing_method') ? <InfoField>
            <InfoLabel>{t('Manufacturing method')}</InfoLabel>
            <InfoValue>
              {info.get('manufacturing_method') === 'made_in_house' ? t('Made in house') : t('Premade')}
            </InfoValue>
          </InfoField> : null}
          {info.get('manufacturing_value_added') ? <InfoField>
            <InfoLabel>{t('Manufacturing value added')}</InfoLabel>
            <InfoValue>{info.get('manufacturing_value_added')}</InfoValue>
          </InfoField> : null}
          {gpsrManufacturers.get(String(info.get('id_manufacturer')))?.get('ca_number') ? <InfoField>
            <InfoLabel>{t('CA Number')}</InfoLabel>
            <InfoValue>{gpsrManufacturers.get(String(info.get('id_manufacturer')))?.get('ca_number')}</InfoValue>
          </InfoField> : null}
          {info.get('hs_code') ? <InfoField>
            <InfoLabel>{t('HS Code')}</InfoLabel>
            <InfoValue>{info.get('hs_code')}</InfoValue>
          </InfoField> : null}
          {info.has('usmca_compliant') ? <InfoField>
            <InfoLabel>{t('USMCA compliant')}</InfoLabel>
            <InfoValue>{info.get('usmca_compliant') ? t('Yes') : t('No')}</InfoValue>
          </InfoField> : null}
        </InfoSectionFields>
      </InfoSection> : null}

      {hasGpsrContact ? <InfoSection>
        <InfoSectionTitle>{t('EU GPSR contact')}</InfoSectionTitle>
        <InfoSectionFields>
          {gpsrContacts.get(String(info.get('id_gpsr_contact')))?.get('name') ? <InfoField>
            <InfoLabel>{t('Representative')}</InfoLabel>
            <InfoValue>{gpsrContacts.get(String(info.get('id_gpsr_contact')))?.get('name')}</InfoValue>
          </InfoField> : null}
          {gpsrContacts.get(String(info.get('id_gpsr_contact')))?.get('address') ? <InfoField>
            <InfoLabel>{t('Address')}</InfoLabel>
            <InfoValue>{gpsrContacts.get(String(info.get('id_gpsr_contact')))?.get('address')}</InfoValue>
          </InfoField> : null}
          {gpsrContacts.get(String(info.get('id_gpsr_contact')))?.get('email') ? <InfoField>
            <InfoLabel>{t('Email')}</InfoLabel>
            <InfoValue>{gpsrContacts.get(String(info.get('id_gpsr_contact')))?.get('email')}</InfoValue>
          </InfoField> : null}
          {gpsrContacts.get(String(info.get('id_gpsr_contact')))?.get('phone_number') ? <InfoField>
            <InfoLabel>{t('Phone number')}</InfoLabel>
            <InfoValue>{gpsrContacts.get(String(info.get('id_gpsr_contact')))?.get('phone_number')}</InfoValue>
          </InfoField> : null}
        </InfoSectionFields>
      </InfoSection> : null}

      {hasSafety ? <InfoSection>
        <InfoSectionTitle>{t('Safety')}</InfoSectionTitle>
        <InfoSectionFields>
          {info.get('flammability_test_year') ? <InfoField>
            <InfoLabel>{t('Flammability test year')}</InfoLabel>
            <InfoValue>{info.get('flammability_test_year')}</InfoValue>
          </InfoField> : null}
          {info.get('risks') ? <InfoField>
            <InfoLabel>{t('Risks')}</InfoLabel>
            <InfoValue>{info.get('risks')}</InfoValue>
          </InfoField> : null}
          {info.get('risk_management') ? <InfoField className="wide">
            <InfoLabel>{t('Risk management')}</InfoLabel>
            <InfoValue>{info.get('risk_management')}</InfoValue>
          </InfoField> : null}
        </InfoSectionFields>
      </InfoSection> : null}

      {info.get('warranty') ? <InfoSection>
        <InfoSectionTitle>{t('Warranty')}</InfoSectionTitle>
        <InfoSectionFields>
          <InfoField className="wide">
            <InfoValue>{info.get('warranty')}</InfoValue>
          </InfoField>
        </InfoSectionFields>
      </InfoSection> : null}
    </>
  }, [countries, gpsrContacts, gpsrManufacturers, t])

  const renderedViewProducts = viewProducts.reduce<RenderedProductInfoViewProduct[]>((list, viewProduct) => {
    const info = viewProduct.get('gpsrInfo')
    if (!info) return list

    const renderedContent = getGpsrContent(info)
    if (!renderedContent) return list

    list.push({
      renderedContent,
      viewProduct
    })

    return list
  }, [])

  return <ProductInfoViewProductList
    emptyMessage={t('No GPSR information is available for this product yet.')}
    renderedViewProducts={renderedViewProducts}
  />
}
